import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { MdClose, MdWarningAmber } from 'react-icons/md';
import api from '../api/axios';
import { formatDateTime } from '../utils/format';

// Shown when an article's status is 'ai_check_flagged' — the background
// duplicate-check gate found a likely match and held it instead of
// publishing automatically. Lets the reviewer see what it matched against
// and either publish anyway or send it back to draft to be reworked.
export default function DuplicateMatchesModal({ articleId, onClose, onResolved }) {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/articles/${articleId}/duplicate-matches`);
      setMatches(res.data.matches || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load matching articles');
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (action) => {
    setResolving(true);
    try {
      await api.post(`/admin/articles/${articleId}/resolve-duplicate`, { action });
      toast.success(action === 'publish_anyway' ? 'Published' : 'Sent back to draft');
      onResolved && onResolved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save decision');
    } finally {
      setResolving(false);
    }
  };

  if (!articleId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ background: '#FFFBEB', padding: '18px 24px', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#FEF3C7', color: '#B45309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            <MdWarningAmber />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#92400E' }}>Possible Duplicate</h3>
            <p style={{ margin: 0, fontSize: 12.5, color: '#B45309', marginTop: 2 }}>
              This article was held back — it looks like it may cover the same story as the article(s) below.
            </p>
          </div>
          <button className="modal-close" onClick={onClose}><MdClose /></button>
        </div>

        <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#475569', fontSize: 14 }}>
              Loading matches...
            </div>
          )}

          {!loading && matches.length === 0 && (
            <div style={{ color: '#475569', fontSize: 14 }}>No match details found.</div>
          )}

          {!loading && matches.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {matches.map((m) => (
                <div key={m.id} style={{ display: 'flex', gap: 12, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 12 }}>
                  {m.image_url && (
                    <img src={m.image_url} alt={m.title} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A', lineHeight: 1.4 }}>{m.title}</div>
                    <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                      Published {formatDateTime(m.published_at)}
                    </div>
                    {m.reason && (
                      <div style={{ fontSize: 12.5, color: '#334155', marginTop: 6, fontStyle: 'italic' }}>"{m.reason}"</div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '3px 8px', borderRadius: 12 }}>
                      {Math.round((m.confidence || 0) * 100)}% match
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={resolving}>Cancel</button>
          <button className="btn btn-outline-red" onClick={() => resolve('send_back_to_draft')} disabled={resolving}>Send Back to Draft</button>
          <button className="btn btn-primary" onClick={() => resolve('publish_anyway')} disabled={resolving}>Publish Anyway</button>
        </div>
      </div>
    </div>
  );
}
